import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ReportGeneratorSetupInputEnum, ScheduledReportPeriodUnits, camelToDisplay, capitalize, getItemTypeToGroupNestedDescendingQueryDef, getNiceDate, makePresentable, pluralize, } from '@iotv/datamodel';
import { ReportGeneratorBundles } from '@iotv/report-generators';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Alert, Autocomplete, Button, Grid, Accordion as MuiAccordion, AccordionDetails as MuiAccordionDetails, AccordionSummary as MuiAccordionSummary, Skeleton, Stack, Typography, accordionSummaryClasses, inputBaseClasses, styled, } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CronExpressionParser from 'cron-parser';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { ScheduledReportsDAO } from '../../data/SystemManagementDAO';
import { Severity } from '../../types';
import { AutosizedTextField } from '../common/AutosizedTextField';
import { ConfirmButton } from '../common/ConfirmButton';
import { SingleEntityAutoQuery } from '../generic/EntityAutoQuery';
import { SingleGroupAutoQuery } from '../generic/GroupAutoQuery';
import { calculateTimeToNextReport, strToMs, strToPeriodUnits } from './scheduledReportSetUpUtil';
const Accordion = styled((props) => _jsx(MuiAccordion, { disableGutters: true, ...props }))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&::before': {
        display: 'none',
    },
}));
const AccordionSummary = styled((props) => (_jsx(MuiAccordionSummary, { expandIcon: _jsx(ArrowForwardIosIcon, { sx: { fontSize: '0.9rem' } }), ...props })))(({ theme }) => ({
    backgroundColor: 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {},
    [`& .${accordionSummaryClasses.content}`]: {
        marginLeft: theme.spacing(1),
    },
}));
const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    borderTop: '1px solid rgba(0, 0, 0, .125)',
    padding: theme.spacing(1),
}));
const debug = process.env.REACT_APP_DEBUG && false;
const locales = ['en', 'en-gb', 'zh-cn', 'de'];
const ScheduledReportUserCard = ({ contextCustomer, contextUser, userFunctions }) => {
    const [locale, setLocale] = useState('en-gb');
    const [allUsers, setAllUsers] = useState([]);
    useEffect(() => {
        if (!contextCustomer)
            return;
        const user_q = getItemTypeToGroupNestedDescendingQueryDef({
            supportedType: 'User',
            groupDefiningObjectTypeId: 'Customer',
            includeSupportedTypeInstances: true,
        });
        ConsumerDao.getGraphQueryRequestV2({ query: user_q, contextObjects: [contextCustomer] }).then((users_res) => {
            if (users_res.err || !users_res.data) {
                console.error('ScheduledReportUserCard: getting users had err', users_res);
                return;
            }
            setAllUsers(users_res.data.getNodesOfType('User'));
        });
    }, [contextCustomer?.sk]);
    /** Get schedules */
    // There is one schedule group for each customer. The name is always schedules_{customerSk}
    // A schedules name is schedule_{userSk}_{reportId} ... this isnt going to work...
    const [schedules, setSchedules] = useState(undefined);
    const [scheduleGroup, setScheduleGroup] = useState(undefined); // Undefined if not checked yet, Null if group doesnt exist
    useEffect(() => {
        if (!contextCustomer)
            return;
        setScheduleGroup(undefined);
        //const groupName = debug ? 'default' : `schedules_${contextCustomer.sk.replace(/:/, '_')}`
        const groupName = `schedules_${contextCustomer.sk.replace(/:/, '_')}`;
        ScheduledReportsDAO.getSchedulesForGroup(groupName).then((res) => {
            if (res.err) {
                if (res.err.message === `Schedule group "${groupName}" does not exist`) {
                    debug && console.log('Group does not exist');
                    //setSchedules([])
                    setScheduleGroup(null);
                }
                else {
                    console.error('ReportSetUpCard: err in listSchedules', res.err);
                }
            }
            if (!res.err) {
                setScheduleGroup(groupName);
            }
            debug && console.log('ReportSetUp: get reports res', res);
            const scheduleInfo = res.data?.schedules.map((schedule) => {
                // Get rate
                const rate_re = new RegExp(/rate\((.+?) (.+?)\)/g);
                const rate_match = rate_re.exec(schedule.ScheduleExpression);
                const generatorId = schedule.Name.split('_')[1];
                const timeOfFirstReportMS = new Date(schedule.StartDate).valueOf();
                // Parse rate string from AWS into millisecond interval
                console.log(schedule);
                let nextEventMS = null;
                if (rate_match) {
                    const width = parseFloat(rate_match[1]);
                    const unit = strToMs[rate_match[2]];
                    const nowMS = Date.now().valueOf();
                    nextEventMS = calculateTimeToNextReport(nowMS, width, unit, timeOfFirstReportMS);
                }
                const creationDateMS = new Date(schedule.CreationDate).valueOf();
                return {
                    Name: schedule.Name,
                    GroupName: schedule.GroupName,
                    Arn: schedule.Arn,
                    userId: schedule.Name.split('_')[0],
                    generatorId,
                    generateLabel: makePresentable(camelToDisplay(generatorId)),
                    creationDateMS,
                    periodUnit: rate_match !== null ? strToPeriodUnits[rate_match[2]] : null,
                    periodWidth: rate_match !== null ? parseFloat(rate_match[1]) : null,
                    inputObj: JSON.parse(schedule.Target.Input).inputs,
                    nextReportMS: nextEventMS,
                };
            });
            setSchedules(scheduleInfo ?? []);
            debug && console.log('ReportSetUp: schedule infos', scheduleInfo);
            //setSchedules((oldSchedules) => [...(oldSchedules ?? []), ...(res.data ?? []).filter((s) => !(oldSchedules ?? []).find((os) => os.Name === s.Name))]) // Ensures if the user somehow makes a schedule before they load that the new one won't be overwritten
        });
    }, [contextCustomer?.sk]);
    /** State for new report subscription */
    const [periodWidth, setPeriodWidth] = useState(1);
    const [periodUnit, setPeriodUnit] = useState(ScheduledReportPeriodUnits.WEEK);
    const [startingAtMs, setStartingAtMs] = useState(Date.now().valueOf());
    const [selectedBundle, setSelectedBundle] = useState(null);
    const [inputObj, setInputObj] = useState(null);
    /** Run when the seleced bundle is changed. Constructs new input object */
    const constructInputObj = (bundle) => {
        const newInputObj = {};
        bundle.inputs.forEach((i) => {
            switch (i.type.inputType) {
                case ReportGeneratorSetupInputEnum.NUMBER:
                    newInputObj[i.key] = 0;
                    break;
                case ReportGeneratorSetupInputEnum.STRING:
                    newInputObj[i.key] = '';
                    break;
                default:
                    newInputObj[i.key] = null;
                    break;
            }
        });
        return newInputObj;
    };
    const updateSelectedBundle = (bundle) => {
        setSelectedBundle(bundle);
        if (bundle?.definedPeriodUnit)
            setPeriodUnit(bundle.definedPeriodUnit);
        if (bundle?.definedPeriodWidth)
            setPeriodWidth(bundle.definedPeriodWidth);
        if (bundle?.definedStartDate) {
            const cronExp = CronExpressionParser.parse(bundle.definedStartDate);
            const next = cronExp.next();
            const nextDateMs = next.toDate().valueOf();
            console.log(nextDateMs);
            setStartingAtMs(nextDateMs);
        }
        if (bundle === null) {
            setInputObj(null);
            return;
        }
        // Construct input obj
        const newInputObj = constructInputObj(bundle);
        debug && console.log('Input Obj', newInputObj);
        setInputObj(newInputObj);
    };
    const updateInputObj = (inputKey, newVal) => {
        setInputObj((oldIO) => {
            const newIO = { ...oldIO };
            newIO[inputKey] = newVal;
            debug && console.log('ReportSetUp: Updated Input Obj', newIO);
            return newIO;
        });
    };
    /** Creates a scheduleg group
     * @param callCreate - because of async-ness, and the fact that we must make a schedule group before we can add to it, it's easier to exit createSchedule early, call this function, and re-call createSchedule if creating the group goes well. For debug reasons you can disabgle it with this param.
     * */
    const createScheduleGroup = (callCreate = false) => {
        if (!contextUser)
            return;
        if (!selectedBundle)
            return;
        if (!contextCustomer)
            return;
        if (inputObj === null)
            return;
        const groupName = `schedules_${contextCustomer.sk.replace(/:/, '_')}`;
        debug && console.log(`Group doesn't exist, creating with name ${groupName}`);
        ScheduledReportsDAO.createScheduleGroup({ groupName }).then((res) => {
            if (res.err) {
                console.error('ReportSetUp: err making group', res.err);
                userFunctions?.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    label: 'Failed to create group',
                    content: 'Failed to create schedule group',
                    severity: Severity.error,
                });
            }
            else if (!res.data) {
                console.error('ReportSetUp: no err nor data?', res);
            }
            else {
                debug && console.log('ReportSetUp: made schedule group:', groupName);
                setScheduleGroup(groupName);
                if (callCreate) {
                    createSchedule(groupName);
                }
            }
        });
    };
    /** Creates a schedule. Detours to create the schedule group first if
     * @param overrideScheduleGroupName - Not usually used, it is used if creating a schedule group works. This ensures that the correct name is used even if react hasnt set the state properly yet
     * */
    const createSchedule = (overrideScheduleGroupName) => {
        if (!contextUser)
            return;
        if (!selectedBundle)
            return;
        if (!contextCustomer)
            return;
        if (inputObj === null)
            return;
        const scheduleGroupName = overrideScheduleGroupName ?? scheduleGroup;
        /** Check for schedule group */
        if (scheduleGroupName === undefined) {
            // Haven't found group yet, aborting.
            return;
        }
        else if (scheduleGroupName === null) {
            debug && console.log('Creating schedule group');
            createScheduleGroup(true); // Due to async jankery this method will re-call create schedule when it's done making the group
            return;
        }
        else {
            debug && console.log(`Adding to group ${scheduleGroupName}`);
        }
        /** Make the schedule */
        debug && console.log(`Submitting schedule (${selectedBundle.id}) with`, inputObj);
        // I originally added the timestamp here but the name has to be <= 64 characters!
        const sequenceForDedupe = schedules?.filter((s) => s.Name.includes(`${contextUser.id}_${selectedBundle.id}`)).length ?? 0;
        const creationDate = Date.now().valueOf();
        const timeOfFirstReportMs = startingAtMs;
        ScheduledReportsDAO.createSchedule({
            name: `${contextUser.id}_${selectedBundle.id}${sequenceForDedupe > 0 ? '_' + sequenceForDedupe : ''}`,
            groupName: scheduleGroupName,
            startingAtMS: startingAtMs,
            periodUnit: periodUnit, // We know it's gonna be this because this function can only be called if validation passes
            periodWidth: periodWidth,
            inputObj: inputObj, // We know it's gonna be this because this function can only be called if validation passes
            userSk: contextUser.sk,
            generatorId: selectedBundle.id,
        }).then((res) => {
            if (res.data) {
                userFunctions?.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    label: 'Created Schedule',
                    content: `Created ${makePresentable(camelToDisplay(selectedBundle.id))} schedule for ${contextUser.name}`,
                    severity: Severity.success,
                });
                const newInfo = {
                    Name: res.data.ScheduleName,
                    GroupName: res.data.GroupName,
                    Arn: 'unknown at this time',
                    periodUnit: periodUnit,
                    periodWidth: periodWidth,
                    creationDateMS: creationDate,
                    nextReportMS: calculateTimeToNextReport(creationDate, periodWidth, strToMs[periodUnit], timeOfFirstReportMs),
                    generatorId: selectedBundle.id,
                    userId: contextUser.id,
                    inputObj: { ...inputObj },
                };
                setSchedules((old) => [...(old ?? []), newInfo]);
                resetNewScheduleState();
            }
        });
    };
    const resetNewScheduleState = () => {
        setInputObj(selectedBundle ? constructInputObj(selectedBundle) : null);
    };
    const deleteSchedule = (info) => {
        ScheduledReportsDAO.deleteSchedule(info.GroupName, info.Name).then((res) => {
            if (res.err) {
                console.error('deleteSchedule', res.err);
            }
            if (res.data) {
                setSchedules((old) => [...(old ?? []).filter((os) => os.Name !== info.Name)]);
                userFunctions?.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    label: 'Delete Schedule',
                    content: `Deleted ${makePresentable(camelToDisplay(info.generatorId))} scheduled report for ${allUsers.find((u) => u.id === info.userId)?.name ?? 'its user'}`,
                    severity: Severity.success,
                });
            }
        });
    };
    /** Validation */
    const validationErrors = useMemo(() => {
        let errors = [];
        // Context User must be loaded, but it's not displayed as a validation error
        if (selectedBundle === null)
            errors.push('Report Generator must be selected');
        if (periodUnit === null)
            errors.push('Period Unit must be specified');
        // Validate inputs
        if (selectedBundle !== null && inputObj !== null) {
            selectedBundle.inputs.forEach((i) => {
                if (inputObj[i.key] === null) {
                    errors.push(`${i.label} must be specified`);
                }
                else if (i.validator !== undefined && inputObj[i.key] !== null) {
                    const validityCheck = i.validator(inputObj[i.key]); // I don't know why this results in never, it shouldn't. Because of how input obj is created we know that this is going to be the right type
                    if (validityCheck.valid === false)
                        errors.push(validityCheck.reason);
                }
            });
        }
        return errors;
    }, [selectedBundle, inputObj, periodUnit]);
    const newSchedulePassesValidation = useMemo(() => {
        return validationErrors.length === 0;
    }, [validationErrors]);
    return (_jsxs(Stack, { gap: 1, children: [_jsx(Typography, { variant: "h5", children: "Scheduled Reports" }), _jsxs(Accordion, { children: [_jsx(AccordionSummary, { children: _jsx(Typography, { children: "Set up new Scheduled Report" }) }), _jsx(AccordionDetails, { children: contextCustomer ? (_jsxs(Stack, { gap: 1, children: [validationErrors.length > 0 && _jsxs(Alert, { severity: "warning", children: ["Schedule is not valid: ", validationErrors.join(', ')] }), _jsxs(Grid, { container: true, direction: "row", spacing: 1, children: [_jsx(Grid, { size: { xs: 12, sm: 12, md: 6, lg: 3 }, children: _jsx(Autocomplete, { size: "small", options: ReportGeneratorBundles, renderInput: (e) => _jsx(AutosizedTextField, { ...e, label: "Report Generator" }), getOptionLabel: (o) => makePresentable(camelToDisplay(o.id)), onChange: (e, o) => updateSelectedBundle(o) }) }), selectedBundle !== null && (_jsxs(_Fragment, { children: [_jsx(Grid, { size: { xs: 4, sm: 3, md: 2, lg: 1 }, children: _jsx(AutosizedTextField, { type: "number", title: "Every", label: "Every", value: periodWidth, onChange: (e) => setPeriodWidth(parseInt(e.target.value)), disabled: selectedBundle?.definedPeriodWidth !== undefined }) }), _jsx(Grid, { size: { xs: 8, sm: 3, md: 3, lg: 1.2 }, children: _jsx(Autocomplete, { size: "small", options: Object.values(ScheduledReportPeriodUnits), value: periodUnit, onChange: (e, v) => setPeriodUnit(v), renderInput: (e) => _jsx(AutosizedTextField, { ...e, label: "Period Unit" }), getOptionLabel: (o) => capitalize(o), disabled: selectedBundle?.definedPeriodUnit !== undefined }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 4, lg: 2 }, children: _jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, adapterLocale: locale, children: _jsx(DateTimePicker, { value: dayjs(startingAtMs), label: "Starting from", sx: { [`& .${inputBaseClasses.input}`]: { paddingBlock: 1, width: '100%' } }, disabled: selectedBundle.definedStartDate !== undefined }) }) })] }))] }), selectedBundle &&
                                    contextCustomer &&
                                    inputObj &&
                                    selectedBundle.inputs.map((input) => (_jsxs(Grid, { container: true, spacing: 1, children: [_jsx(Grid, { size: { xs: 3, md: 2, lg: 1 }, sx: { display: 'flex', alignItems: 'center' }, children: _jsx(Typography, { children: input.label }) }), _jsxs(Grid, { size: { xs: 9, md: 5 }, children: [input.type.inputType == ReportGeneratorSetupInputEnum.STRING && (_jsx(AutosizedTextField, { value: inputObj[input.key], onChange: (e) => {
                                                            updateInputObj(input.key, e.target.value);
                                                        } })), input.type.inputType == ReportGeneratorSetupInputEnum.NUMBER && (_jsx(AutosizedTextField, { value: inputObj[input.key], type: "number", onChange: (e) => {
                                                            updateInputObj(input.key, parseFloat(e.target.value));
                                                        } })), input.type.inputType == ReportGeneratorSetupInputEnum.VALIDBUSINESSOBJECT && (_jsx(SingleEntityAutoQuery, { entityType: input.type.vobType, contextObject: contextCustomer, currentEntity: inputObj[input.key], setCurrentEntity: (g) => {
                                                            updateInputObj(input.key, g);
                                                        } })), input.type.inputType == ReportGeneratorSetupInputEnum.GROUP && (_jsx(SingleGroupAutoQuery, { entityType: input.type.supportedType, contextObject: contextCustomer, currentGroup: inputObj[input.key], setCurrentGroup: (g) => {
                                                            updateInputObj(input.key, g);
                                                        } }))] }), _jsx(Grid, { size: { xs: 12, md: 5, lg: 6 }, children: input.description && (_jsx(Alert, { severity: "info", sx: { paddingBlock: 0, alignItems: 'center', height: '100%' }, children: input.description })) })] }, input.key))), _jsx(Grid, { container: true, children: _jsx(Grid, { size: { xs: 12 }, sx: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }, children: _jsx(Button, { variant: "contained", color: "success", disabled: !newSchedulePassesValidation || !contextUser, onClick: () => {
                                                createSchedule();
                                            }, children: "Schedule Report" }) }) })] })) : (_jsx(Skeleton, {})) })] }), _jsx(Typography, { variant: "h5", children: "Existing Reports" }), _jsxs(Stack, { children: [schedules && schedules.filter((s) => s.userId === contextUser?.id).length === 0 && _jsx(Alert, { severity: "info", children: "You have no schedules set up" }), schedules ? (schedules
                        .filter((s) => s.userId === contextUser?.id)
                        .map((s) => {
                        let pUnit = s.periodUnit;
                        let pWidth = s.periodWidth;
                        if (pUnit && pWidth && s.periodUnit === ScheduledReportPeriodUnits.DAY && pWidth % 7 === 0) {
                            pWidth /= 7;
                            pUnit = ScheduledReportPeriodUnits.WEEK;
                        }
                        return (_jsxs(Accordion, { children: [_jsx(AccordionSummary, { children: _jsxs(Grid, { container: true, sx: { width: '100%' }, children: [_jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsxs(Typography, { children: ["User: ", allUsers.find((u) => u.id === s.userId)?.name ?? 'Loading...'] }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsxs(Typography, { children: ["Report: ", makePresentable(camelToDisplay(s.generatorId))] }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2 }, children: _jsxs(Typography, { children: ["Every: ", pWidth ?? '-', " ", pUnit ? (pWidth === 1 ? pUnit : pluralize(pUnit)) : ''] }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsxs(Typography, { children: ["Next Report: ", s.nextReportMS && getNiceDate(s.nextReportMS)] }) })] }) }), _jsx(AccordionDetails, { children: _jsxs(Stack, { gap: 1, children: [_jsxs(Grid, { container: true, children: [_jsx(Grid, { size: { xs: 12, sm: 8, lg: 10 }, sx: { display: 'flex', alignItems: 'center' }, children: _jsxs(Typography, { children: ["Created: ", getNiceDate(s.creationDateMS)] }) }), _jsx(Grid, { size: { xs: 12, sm: 4, lg: 2 }, children: _jsx(Stack, { children: _jsx(ConfirmButton, { variant: "outlined", onConfirm: () => deleteSchedule(s), children: "Delete Schedule" }) }) })] }), _jsxs(Stack, { direction: "row", spacing: 1, sx: { alignItems: 'center' }, children: [_jsx(Typography, { fontWeight: 200, children: "Inputs" }), Object.entries(s.inputObj).map(([inputKey, val], i) => (_jsx(_Fragment, { children: _jsxs(Stack, { direction: "row", spacing: 1, sx: {
                                                                border: (t) => `solid 1px ${t.palette.primary.main}`,
                                                                alignItems: 'center',
                                                                paddingLeft: 0.5,
                                                                paddingRight: 1,
                                                                borderRadius: 1,
                                                            }, children: [_jsx(Typography, { sx: {
                                                                        color: (t) => t.palette.primary.main,
                                                                        padding: 0.5,
                                                                        paddingRight: 1,
                                                                        borderRight: (t) => `solid 1px ${t.palette.primary.main}`,
                                                                    }, children: makePresentable(inputKey) }), typeof val === 'string' || (typeof val === 'number' && _jsx(Typography, { children: typeof val === 'string' ? val : '' })), typeof val === 'object' && _jsx(Typography, { children: typeof val === 'object' ? val.name : '' })] }) })))] })] }) })] }, s.Name));
                    })) : (_jsxs(Stack, { gap: 1, children: [_jsx(Skeleton, { variant: "rounded", height: 50 }), _jsx(Skeleton, { variant: "rounded", height: 50 }), _jsx(Skeleton, { variant: "rounded", height: 50 })] }))] })] }));
};
export default ScheduledReportUserCard;
